import { useEffect, useImperativeHandle, useRef, useState } from "react";
import braintree, { HostedFields, HostedFieldsEvent } from "braintree-web";
import { Stack } from "@/components/ui/Stack";
import { FormControl } from "@/components/ui/FormControl";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import classes from "./BraintreeCardDetails.module.scss";
import { clsx } from "clsx";
import { BraintreeCardDetailsProps } from "./BraintreeCardDetailsProps";
import { useBraintree } from "./useBraintree";
import { trackError } from "@/utils/error-monitoring";
import { useCardDetailsValidation } from "./useCardDetailsValidation";
import { useCardDetailsErrorsStore } from "./useCardDetailsErrorsStore";
import { useTranslation } from "@/hooks/useTranslation";
import { CheckSharp } from "@/components/ui/Icon/CheckSharp";
import { InputAdornment } from "@/components/ui/InputAdornment";

export function BraintreeCardDetails(
  props: Readonly<BraintreeCardDetailsProps>,
) {
  const { className, ref, amount } = props;

  const { validate, validateField, validFields } =
    useCardDetailsValidation(amount);
  const isValidated = useCardDetailsErrorsStore((state) => state.isValidated);
  const errors = useCardDetailsErrorsStore((state) => state.errors);
  const setIsValidated = useCardDetailsErrorsStore(
    (state) => state.setIsValidated,
  );
  const resetErrors = useCardDetailsErrorsStore((state) => state.resetErrors);

  const { braintreeClient } = useBraintree();
  const [braintreeInstance, setBraintreeInstance] = useState<HostedFields>();
  const [loading, setLoading] = useState(true);
  const [setupFailed, setSetupFailed] = useState(false);
  const [setupAttempt, setSetupAttempt] = useState(0);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const t = useTranslation("card_details");
  const tCommon = useTranslation("_common");

  const instanceRef = useRef<HostedFields | null>(null);
  const creatingRef = useRef(false);
  const createdForClientRef = useRef<typeof braintreeClient | null>(null);
  const teardownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const validateFieldRef = useRef(validateField);
  validateFieldRef.current = validateField;

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const errors = await validate(braintreeInstance);

      if (!errors) {
        return {
          isValid: true,
        };
      }

      if (errors.number) {
        braintreeInstance?.focus("number");
      } else if (errors.expirationDate) {
        braintreeInstance?.focus("expirationDate");
      } else if (errors.cvv) {
        braintreeInstance?.focus("cvv");
      }

      return {
        isValid: false,
      };
    },
  }));

  useEffect(() => {
    if (!braintreeClient) return;

    // Cancel a teardown left pending by React StrictMode's mount/unmount/mount
    // in dev so we keep and reuse the fields already being created.
    if (teardownTimerRef.current) {
      clearTimeout(teardownTimerRef.current);
      teardownTimerRef.current = null;
    }

    const initializeBraintree = async () => {
      // Same client, just a remount — reuse the existing hosted fields.
      if (
        instanceRef.current &&
        createdForClientRef.current === braintreeClient
      ) {
        setBraintreeInstance(instanceRef.current);
        setLoading(false);
        return;
      }

      // A create() is already in flight (StrictMode double invoke). Bail so we
      // never create twice into the same container, which rejects with
      // HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME and leaves the card fields broken
      // and the submit button stuck.
      if (creatingRef.current) return;

      creatingRef.current = true;
      try {
        const instance = await braintree.hostedFields.create({
          client: braintreeClient,
          styles: {
            "::placeholder": {
              color: "rgba(0, 0, 0, 0.3)",
            },
            input: {
              "font-size": "1rem",
              "font-family": "system-ui, sans-serif",
              color: "#333333",
            },
          },
          fields: {
            number: {
              selector: "#card-number",
              placeholder: "0000 0000 0000 0000",
            },
            cvv: {
              selector: "#cvv",
              placeholder: "000",
            },
            expirationDate: {
              selector: "#expiration-date",
              placeholder: "MM/YY",
            },
          },
        });

        instanceRef.current = instance;
        createdForClientRef.current = braintreeClient;
        setLoading(false);
        setBraintreeInstance(instance);

        instance.on("blur", () => {
          setFocusedField(null);
        });

        instance.on("focus", (event: HostedFieldsEvent) => {
          setFocusedField(event.emittedBy);
        });

        instance.on("validityChange", (event: HostedFieldsEvent) => {
          validateFieldRef.current(event);
        });
      } catch (error) {
        trackError(error as Error);
        setLoading(false);
        setSetupFailed(true);
      } finally {
        creatingRef.current = false;
      }
    };

    initializeBraintree();

    const teardownInstance = () => {
      if (instanceRef.current) {
        instanceRef.current
          .teardown()
          .catch((error) => trackError(error as Error));
        instanceRef.current = null;
        createdForClientRef.current = null;
      }
    };

    return () => {
      // Defer teardown so an immediate StrictMode remount can cancel it; a real
      // unmount lets it run and releases the Braintree iframes.
      teardownTimerRef.current = setTimeout(teardownInstance, 0);
    };
  }, [braintreeClient, setupAttempt]);

  const retrySetup = () => {
    // The Try again button unmounts below; without this, keyboard and screen
    // reader users are dropped on <body>.
    wrapperRef.current?.focus();
    setIsValidated(false);
    resetErrors();
    setSetupFailed(false);
    setLoading(true);
    setSetupAttempt((attempt) => attempt + 1);
  };

  const errorNumber = isValidated ? errors.number : null;
  const errorExpirationDate = isValidated ? errors.expirationDate : null;
  const errorCvv = isValidated ? errors.cvv : null;

  // Leaving the field containers out drops the iframes a failed create() left
  // in them: it never returns the instance that could tear them down, and a
  // retry into the same containers rejects with HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME.
  if (setupFailed) {
    return (
      <div
        ref={wrapperRef}
        tabIndex={-1}
        className={clsx(classes.wrapper, className)}
      >
        <Stack gap="md" align="flex-start">
          <Alert variant="error">{tCommon("3ds-unavailable")}</Alert>
          <Button onClick={retrySetup}>{tCommon("try-again")}</Button>
        </Stack>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      tabIndex={-1}
      className={clsx(classes.wrapper, className)}
      aria-live="polite"
      aria-busy={loading}
    >
      {loading && (
        <div className={classes.loader}>
          <Spinner />
        </div>
      )}

      <Stack gap="md">
        <FormControl
          htmlFor="card-number"
          label={t("card-number")}
          error={errorNumber}
          className={classes.formControl}
          required
        >
          <div
            id="card-number"
            className={clsx({
              [classes.input]: true,
              [classes.withIcon]: true,
              [classes.ccNumber]: true,
              [classes.error]: errorNumber,
              [classes.focused]: focusedField === "number",
            })}
          />

          {validFields.number && (
            <InputAdornment position="end" isInteractive={false}>
              <CheckSharp
                size={24}
                strokeWidth={4}
                stroke="currentColor"
                className={classes.check}
              />
            </InputAdornment>
          )}
        </FormControl>

        <Stack direction="row" gap="lg">
          <FormControl
            htmlFor="expiration-date"
            label={t("expiration-date")}
            error={errorExpirationDate}
            className={classes.formControl}
            required
          >
            <div
              id="expiration-date"
              className={clsx({
                [classes.input]: true,
                [classes.withIcon]: true,
                [classes.date]: true,
                [classes.error]: errorExpirationDate,
                [classes.focused]: focusedField === "expirationDate",
              })}
            />

            {validFields.expirationDate && (
              <InputAdornment position="end" isInteractive={false}>
                <CheckSharp
                  size={24}
                  strokeWidth={4}
                  stroke="currentColor"
                  className={classes.check}
                />
              </InputAdornment>
            )}
          </FormControl>

          <FormControl
            htmlFor="cvv"
            label={t("cvv")}
            error={errorCvv}
            className={classes.formControl}
            required
          >
            <div
              id="cvv"
              className={clsx({
                [classes.input]: true,
                [classes.withIcon]: true,
                [classes.cvv]: true,
                [classes.error]: errorCvv,
                [classes.focused]: focusedField === "cvv",
              })}
            />

            {validFields.cvv && (
              <InputAdornment position="end" isInteractive={false}>
                <CheckSharp
                  size={24}
                  strokeWidth={4}
                  stroke="currentColor"
                  className={classes.check}
                />
              </InputAdornment>
            )}
          </FormControl>
        </Stack>
      </Stack>
    </div>
  );
}
